/**
 * @component
 * @name Auth
 * @description
 * The `Auth` component serves as the main authentication page for users. It displays the Open Carbon Protocol logo, a welcome message,
 * and a tabbed interface for logging in or creating a new account. It also provides a link to reset the password.
 *
 * @returns {JSX.Element} - The component rendering the authentication interface.
 *
 * @example
 * // Example usage of Auth:
 * <Auth />
 *
 * @see {@link SEO} - Shared SEO component for setting page metadata.
 * @see {@link SignIn} - Component for user sign-in.
 * @see {@link SignUp} - Component for user sign-up.
 * @see {@link CustomTab} - Dynamic import of tab component with `ssr` set to `false`.
 *
 * @author @Geektrovert <samnan@oneshot.earth>
 * @author @ProdiptaOneShotEarth <prodipta@oneshot.earth>
 */

import dynamic from "next/dynamic";
import Image from "next/image";
import Link from "next/link";
import { HiOutlineExclamationCircle } from "react-icons/hi";

import SignIn from "@/components/auth/sign-in";
import SEO from "@/components/shared/SEO";

const CustomTab = dynamic(() => import("@/components/shared/ui/Tabs"), { ssr: false });

const Auth = () => {
	const showDevStagingWarning = process.env.NEXT_PUBLIC_SHOW_DEV_STAGING_WARNING_IN_AUTH_PAGE === "true";

	return (
		<>
			<SEO title="Authentication" />
			<div className="mt-[15vh] flex min-h-screen flex-col items-center space-y-4 transition-all">
				<Image src="/logo-grass.svg" alt="logo" width={200} height={200} className="w-24" />
				<h2 className="ocp-heading-text max-w-[30ch] text-center">Welcome to the Open Carbon Protocol</h2>
				{showDevStagingWarning && (
					<p className="max-w-[50ch] rounded-md border border-sand-500 bg-sand-200 p-6 text-center text-sm text-sand-900">
						<HiOutlineExclamationCircle
							aria-hidden="true"
							className="mr-2 inline-block text-base text-sand-900"
							size={24}
						/>
						You are viewing a development environment. This version is intended for devlopement and testing purposes
						only. For regular use please visit the production environment at{" "}
						<Link href="https://pilot.ocp.earth" className="text-clearSky-700 !underline hover:text-clearSky-500">
							https://pilot.ocp.earth
						</Link>
					</p>
				)}
				<div className="w-[40ch] max-w-[90%]">
					<CustomTab labels={["Log In"]} tabHeaderOverride={"w-full"}>
						<SignIn />
						{/*<SignUp />*/}
						<></>
					</CustomTab>
				</div>

				<Link href="/auth/request-password-reset" className="text-clearSky-700 hover:text-clearSky-500">
					Reset password
				</Link>
			</div>
		</>
	);
};

export default Auth;
